import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type CgmDisplayMode = 'standard' | 'expert';

interface CgmDisplayModeContextValue {
  mode: CgmDisplayMode;
  isExpert: boolean;
  setMode: (mode: CgmDisplayMode) => void;
}

const STORAGE_KEY = 'soverdi_user_mode';
export const STANDARD_MODE: CgmDisplayMode = 'standard';
export const EXPERT_MODE: CgmDisplayMode = 'expert';

const readInitialMode = (): CgmDisplayMode => {
  try {
    const storedMode = window.localStorage.getItem(STORAGE_KEY);
    if (storedMode === EXPERT_MODE) return EXPERT_MODE;
    return STANDARD_MODE;
  } catch {
    return STANDARD_MODE;
  }
};

const CgmDisplayModeContext = createContext<CgmDisplayModeContextValue | undefined>(undefined);

export const CgmDisplayModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<CgmDisplayMode>(readInitialMode);

  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.soverdi-clone-root');
    if (!root) return undefined;
    root.setAttribute('data-soverdi-mode', mode);
    return () => {
      if (root.getAttribute('data-soverdi-mode') === mode) {
        root.removeAttribute('data-soverdi-mode');
      }
    };
  }, [mode]);

  const setMode = (nextMode: CgmDisplayMode) => {
    setModeState(nextMode);
    try {
      window.localStorage.setItem(STORAGE_KEY, nextMode);
    } catch {
      // Display preference persistence is best-effort only.
    }
  };

  const value = useMemo(
    () => ({ mode, isExpert: mode === EXPERT_MODE, setMode }),
    [mode],
  );

  return (
    <CgmDisplayModeContext.Provider value={value}>
      {children}
    </CgmDisplayModeContext.Provider>
  );
};

export const useCgmDisplayMode = (): CgmDisplayModeContextValue => {
  const context = useContext(CgmDisplayModeContext);
  if (!context) {
    throw new Error('useCgmDisplayMode must be used inside CgmDisplayModeProvider');
  }
  return context;
};
